import React from 'react';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '@cotera/client/app/components/ui/icon';
import {
  ALIGNMENT_CLASS,
  formatDataGridValue,
  inCellBarShare,
} from '../format';
import { DEFAULT_DATA_GRID_DISPLAY_OPTIONS } from '../types';
import { DEFAULT_DATA_GRID_ROW_HEIGHT } from '../types';
import type { DataGridCellComponentProps, DataGridRowHeight } from '../types';
import { DataGridCellEditor, dataGridEditorKind } from './cell-editor';
import { DataGridCellDetailEnabledContext } from './cell-detail';
import { HighlightedText } from '../highlight';

/**
 * The grid's row size, for the cells. Read through context rather than passed
 * per cell: it is one value for the whole grid, and a cell only needs it to
 * decide whether it has the room to wrap.
 */
export const DataGridRowHeightContext = React.createContext<DataGridRowHeight>(
  DEFAULT_DATA_GRID_ROW_HEIGHT
);

/** Marks a cell that carries an unsaved edit. */
export const DataGridDirtyNotch = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[7px] border-t-[7px] border-l-transparent border-t-primary"
  />
);

export function BaseCell<TRow>({
  context,
  style,
  onClick,
}: DataGridCellComponentProps<TRow>) {
  const cellDetailEnabled = React.useContext(DataGridCellDetailEnabledContext);
  // Tall rows exist to show what a single line was cutting off, so they are
  // also the only size where a cell wraps. The other two keep one truncated
  // line, which is what makes a dense grid scannable.
  const wraps = React.useContext(DataGridRowHeightContext) === 'tall';
  const options = context.displayOptions ?? DEFAULT_DATA_GRID_DISPLAY_OPTIONS;
  const formatted = formatDataGridValue(
    context.value,
    options,
    context.column.type
  );
  const barShare = inCellBarShare(context.value, options);
  const editOptions = context.state.editing
    ? context.column.getEditOptions?.()
    : undefined;
  const rendered =
    context.column.renderCell?.(context) ??
    (formatted === null ? (
      <span className="text-muted-foreground">null</span>
    ) : (
      // Only the default text path is marked. A column that renders itself owns
      // its own presentation, and a search term is not a reason to go looking
      // for substrings inside someone else's markup.
      <HighlightedText text={formatted} />
    ));

  return (
    <div
      ref={(element) => {
        context.anchorRef.current = element;
      }}
      id={`dg-cell-${context.address.rowIndex}-${context.address.columnIndex}`}
      role="gridcell"
      aria-colindex={context.address.columnIndex + 2}
      aria-selected={context.state.selected}
      data-focused={context.state.focused ? 'true' : 'false'}
      data-selected={context.state.cellSelected ? 'true' : 'false'}
      data-editing={context.state.editing ? 'true' : 'false'}
      data-dirty={context.state.dirty ? 'true' : 'false'}
      className={cn(
        'group/cell absolute flex h-full overflow-hidden border-b border-r border-border/70 px-3 text-sm outline-none data-[focused=true]:ring-2 data-[focused=true]:ring-primary/70 data-[focused=true]:ring-inset data-[selected=true]:bg-primary/10',
        wraps ? 'items-start py-2' : 'items-center',
        // The editing ring wins over focus/selection styling and lifts the
        // cell above its neighbours so the ring is never clipped.
        'data-[editing=true]:z-30 data-[editing=true]:overflow-visible data-[editing=true]:bg-background data-[editing=true]:ring-2 data-[editing=true]:ring-primary data-[editing=true]:ring-inset',
        ALIGNMENT_CLASS[options.alignment]
      )}
      style={style}
      title={context.state.editing ? undefined : formatted ?? undefined}
      onMouseDown={(event) => {
        if (event.button !== 0 || context.state.editing) {
          return;
        }
        context.commands.focus();
      }}
      onClick={context.state.editing ? undefined : onClick}
      onDoubleClick={(event) => {
        if (context.state.editable) {
          event.stopPropagation();
          context.commands.beginEdit();
          return;
        }
        // The expand modal is the one detail surface where the grid offers it:
        // it hosts a column's own `renderDetail` as readily as the built-in
        // value view, and a panel of per-row actions wants the room a modal has
        // and an overlay anchored under a 128px cell does not. Grids with the
        // feature off keep the anchored overlay as their only option.
        if (cellDetailEnabled) {
          event.stopPropagation();
          context.commands.expandCell();
        } else if (context.column.renderDetail !== undefined) {
          context.commands.openDetail();
        }
      }}
    >
      {context.state.editing ? (
        <DataGridCellEditor
          value={context.value}
          kind={
            context.column.editorKind ??
            dataGridEditorKind(context.column.type, editOptions)
          }
          options={editOptions}
          isBoolean={context.column.type === 'boolean'}
          seed={context.state.editSeed}
          onCommit={context.commands.commitEdit}
          onCancel={context.commands.cancelEdit}
        />
      ) : (
        <>
          {barShare !== null ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-1 left-1 rounded-sm bg-primary/15"
              style={{ width: `calc(${barShare * 100}% - 0.5rem)` }}
            />
          ) : null}
          <div
            className={cn(
              'relative min-w-0',
              // Clamped rather than merely clipped: a row that ends mid-line
              // reads as broken, and the ellipsis says there is more to see in
              // the cell's detail view.
              wraps
                ? 'w-full whitespace-pre-wrap break-words line-clamp-3'
                : 'truncate'
            )}
          >
            {rendered}
          </div>
          {cellDetailEnabled ? (
            <button
              type="button"
              aria-label="Expand cell"
              // Hidden until the cell is hovered or focused, so it never
              // competes with the value for the little space a cell has.
              tabIndex={-1}
              className="absolute right-1 top-1/2 z-10 hidden -translate-y-1/2 rounded border border-border bg-background/90 p-0.5 text-muted-foreground shadow-sm hover:text-foreground group-hover/cell:block group-data-[focused=true]/cell:block"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                context.commands.expandCell();
              }}
            >
              <Icon icon="arrows-pointing-out" size="small" />
            </button>
          ) : null}
          {context.state.dirty ? <DataGridDirtyNotch /> : null}
        </>
      )}
    </div>
  );
}
